import { useEffect, useId, useMemo, useRef, useState } from "react";
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';

type SearchableSelectOption = {
  value: string | number;
  label: string;
};

type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  ariaLabel?: string;
  includeEmptyOption?: boolean;
  emptyOptionLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
};

function SearchableSelect({
  options,
  value,
  onChange,
  ariaLabel = "Select",
  includeEmptyOption = false,
  emptyOptionLabel = "None",
  disabled = false,
  placeholder = "Select an option",
  searchPlaceholder = "Search..."
}: SearchableSelectProps) {
  const generatedId = useId().replace(/:/g, "");
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState("");
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (!show) {
      setSearch("");
      if (wasOpen.current) {
        toggleRef.current?.focus();
      }
    }
    wasOpen.current = show;
  }, [show]);

  const normalizedValue = value === null || value === undefined ? "" : String(value);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
  }, [options, search]);

  const selectedOption = options.find((option) => String(option.value) === normalizedValue);
  const selectedLabel = normalizedValue === "" && includeEmptyOption
    ? emptyOptionLabel
    : selectedOption?.label || placeholder;

  return (
    <Dropdown show={show} onToggle={(nextShow) => setShow(!!nextShow)} className="searchable-select">
      <Dropdown.Toggle
        ref={toggleRef}
        id={`searchable-select-${generatedId}`}
        aria-label={ariaLabel}
        className={`searchable-select-toggle ${disabled ? 'searchable-select-toggle-disabled' : ''}`}
        disabled={disabled}
      >
        <span className={`searchable-select-label ${!selectedOption && normalizedValue !== "" ? 'searchable-select-label-placeholder' : ''}`}>
          {selectedLabel}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="searchable-select-menu">
        <div className="searchable-select-search-wrapper" onClick={(event) => event.stopPropagation()}>
          <Form.Control
            type="search"
            placeholder={searchPlaceholder}
            aria-label={`${ariaLabel} search`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setShow(false);
              }
            }}
            autoFocus
          />
        </div>
        <div className="searchable-select-options">
          {includeEmptyOption ? (
            <Dropdown.Item
              as="button"
              type="button"
              active={normalizedValue === ""}
              onClick={() => {
                onChange("");
                setShow(false);
              }}
            >
              {emptyOptionLabel}
            </Dropdown.Item>
          ) : null}
          {filteredOptions.map((option) => (
            <Dropdown.Item
              as="button"
              type="button"
              key={String(option.value)}
              active={String(option.value) === normalizedValue}
              onClick={() => {
                onChange(String(option.value));
                setShow(false);
              }}
            >
              {option.label}
            </Dropdown.Item>
          ))}
          {filteredOptions.length === 0 ? (
            <div className="searchable-select-empty">No matches found.</div>
          ) : null}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default SearchableSelect;
